<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Dialog } from "../../../../base-components/Headless";
import Button from "../../../../base-components/Button";
import { FormInput } from "../../../../base-components/Form";
import Table from "../../../../base-components/Table";
import { numeroFormateado } from "../../../../utils/helper";
import type { Pedido, PedidoItem } from "@fixeduy/restaurantestypes";
import { estadosPedidoItem } from "@fixeduy/restaurantestypes";
import { usePedidosStore } from "../../../../services/pedidos";
import { useMensajesStore } from "../../../../services/mensajes";

interface Props {
  pedido: Pedido;
  items: PedidoItem[];
}

const props = defineProps<Props>();

const pedidosStore = usePedidosStore();
const mensajesStore = useMensajesStore();

const emit = defineEmits<{
  (e: "cerrar"): void;
  (e: "actualizar-items", items: PedidoItem[]): void;
}>();

const guardando = ref(false);

const itemsLocal = ref<PedidoItem[]>([]);

watch(
  () => props.items,
  items => {
    itemsLocal.value = items.map(i => ({ ...i }));
  },
  { immediate: true, deep: true }
);

const totalItems = computed(() =>
  itemsLocal.value.reduce((acc, item) => acc + (item.cantidad ?? 0), 0)
);

const subtotal = computed(() =>
  itemsLocal.value.reduce((acc, item) => acc + (item.subtotal ?? 0), 0)
);
const iva = computed(() =>
  itemsLocal.value.reduce((acc, item) => acc + (item.montoIva ?? 0), 0)
);
const total = computed(() =>
  itemsLocal.value.reduce((acc, item) => acc + (item.total ?? 0), 0)
);

const clampCantidad = (n: number) => {
  if (Number.isNaN(n) || !Number.isFinite(n)) return 1;
  return Math.max(1, Math.floor(n));
};

const recalcMontosItem = (idx: number) => {
  const it = itemsLocal.value[idx];
  if (!it) return;

  const cantidad = clampCantidad(Number(it.cantidad ?? 1));
  it.cantidad = cantidad;

  const precioConIva = Number(it.precioUnitarioConIva ?? 0);
  const precioSinIva = Number(it.precioUnitarioSinIva ?? 0);

  it.subtotal = Number((precioSinIva * cantidad).toFixed(2));
  it.total = Number((precioConIva * cantidad).toFixed(2));
  it.montoIva = Number((it.total - it.subtotal).toFixed(2));
};

/** Persiste un ítem pendiente en Firestore (cantidad, montos, observaciones). */
const writePendienteItemDoc = async (idx: number): Promise<boolean> => {
  const it = itemsLocal.value[idx];
  if (!it || (it.estado ?? "").toLowerCase() !== "pendiente") return true;

  recalcMontosItem(idx);

  return pedidosStore.updatePedidoItem(props.pedido.id, it.id, {
    cantidad: it.cantidad,
    observaciones: it.observaciones ?? "",
    subtotal: it.subtotal,
    montoIva: it.montoIva,
    total: it.total
  });
};

/** Actualiza totales del pedido padre y sincroniza estado local con GestionPedidos. */
const flushTotalesYEmit = async (): Promise<boolean> => {
  const subtotal = itemsLocal.value.reduce(
    (acc, item) => acc + (item.subtotal ?? 0),
    0
  );
  const montoIva = itemsLocal.value.reduce(
    (acc, item) => acc + (item.montoIva ?? 0),
    0
  );
  const total = itemsLocal.value.reduce(
    (acc, item) => acc + (item.total ?? 0),
    0
  );

  const ok = await pedidosStore.updateTotalesPedido(props.pedido.id, {
    subtotal,
    montoIva,
    total,
    estado: props.pedido.estado
  });
  if (!ok) return false;

  emit(
    "actualizar-items",
    itemsLocal.value.map(i => ({ ...i }))
  );
  return true;
};

const actualizarItem = async (idx: number) => {
  const okItem = await writePendienteItemDoc(idx);
  if (!okItem) {
    mensajesStore.mostrarModal(
      "Error al guardar",
      "No se pudo actualizar la cantidad en el pedido. Intentá nuevamente.",
      mensajesStore.mensajeType.error
    );
    return;
  }
  const okTot = await flushTotalesYEmit();
  if (!okTot) {
    mensajesStore.mostrarModal(
      "Error al guardar totales",
      "El ítem se guardó pero no se pudieron actualizar los totales del pedido.",
      mensajesStore.mensajeType.error
    );
  }
};

const onObservacionesBlur = async (idx: number) => {
  const okItem = await writePendienteItemDoc(idx);
  if (!okItem) {
    mensajesStore.mostrarModal(
      "Error al guardar",
      "No se pudo guardar la nota del producto. Intentá nuevamente.",
      mensajesStore.mensajeType.error
    );
    return;
  }
  await flushTotalesYEmit();
};

/** Cierra el modal guardando ítems pendientes en Firestore (volver a editar el pedido en pantalla). */
const guardarYVolver = async () => {
  guardando.value = true;
  try {
    const results = await Promise.all(
      itemsLocal.value.map((_, idx) => writePendienteItemDoc(idx))
    );
    if (results.some(r => !r)) {
      mensajesStore.mostrarModal(
        "Error al guardar",
        "No se pudieron guardar los cambios del pedido. Intentá nuevamente.",
        mensajesStore.mensajeType.error
      );
      return;
    }
    const okTot = await flushTotalesYEmit();
    if (!okTot) {
      mensajesStore.mostrarModal(
        "Error al guardar totales",
        "No se pudieron actualizar los totales del pedido.",
        mensajesStore.mensajeType.error
      );
      return;
    }
    emit("cerrar");
  } finally {
    guardando.value = false;
  }
};

const badgeClassesEstadoItem = (estado?: string) => {
  const e = estadosPedidoItem.find(o => o.value === (estado as any));
  return e?.badgeClasses ?? "";
};

const eliminarItem = async (item: PedidoItem) => {
  // Solo se puede eliminar si NO fue enviado a cocina (pendiente).
  if ((item.estado ?? "").toLowerCase() !== "pendiente") return;

  const pedidoId = props.pedido.id;
  const ok = await pedidosStore.borrarPedidoItem(pedidoId, item.id);
  if (!ok) {
    mensajesStore.mostrarModal(
      "Error al eliminar",
      "No se pudo eliminar el producto del pedido. Intentá nuevamente.",
      mensajesStore.mensajeType.error
    );
    return;
  }

  const next = itemsLocal.value.filter(it => it.id !== item.id);

  // Persistimos totales en base para que el pedido quede consistente.
  const subtotal = next.reduce((acc, it) => acc + (it.subtotal ?? 0), 0);
  const montoIva = next.reduce((acc, it) => acc + (it.montoIva ?? 0), 0);
  const total = next.reduce((acc, it) => acc + (it.total ?? 0), 0);

  await pedidosStore.updateTotalesPedido(pedidoId, {
    subtotal,
    montoIva,
    total,
    estado: props.pedido.estado
  });

  itemsLocal.value = next;
  emit(
    "actualizar-items",
    itemsLocal.value.map(i => ({ ...i }))
  );

  mensajesStore.mostrarToast(
    "Ítem eliminado",
    "",
    mensajesStore.mensajeType.exito
  );
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="xxl">
    <Dialog.Panel class="overflow-hidden flex flex-col max-h-[85vh]">
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          Pedido en curso · Mesa {{ props.pedido.mesaNombre }}
        </h2>
      </Dialog.Title>

      <Dialog.Description>
        <div class="text-xs text-slate-500 mb-3">
          {{ totalItems }} producto(s)
        </div>

        <div class="overflow-x-auto max-h-[50vh]">
          <Table hover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th class="whitespace-nowrap">Cantidad</Table.Th>
                <Table.Th class="whitespace-nowrap">Producto</Table.Th>
                <Table.Th class="whitespace-nowrap">Nota</Table.Th>
                <Table.Th class="whitespace-nowrap">Precio</Table.Th>
                <Table.Th class="whitespace-nowrap">Estado</Table.Th>
                <Table.Th class="whitespace-nowrap text-right"
                  >Acciones</Table.Th
                >
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr
                v-for="(it, idx) in itemsLocal"
                :key="it.id"
                class="align-top"
              >
                <Table.Td class="whitespace-nowrap w-24">
                  <FormInput
                    type="number"
                    min="1"
                    step="1"
                    v-model="it.cantidad"
                    @change="actualizarItem(idx)"
                    :disabled="(it.estado ?? '').toLowerCase() !== 'pendiente'"
                  />
                </Table.Td>
                <Table.Td class="min-w-[260px]">
                  <FormInput type="text" :value="it.nombre" disabled />
                </Table.Td>
                <Table.Td class="min-w-[260px]">
                  <FormInput
                    type="text"
                    v-model="it.observaciones"
                    @blur="onObservacionesBlur(idx)"
                    :disabled="(it.estado ?? '').toLowerCase() !== 'pendiente'"
                  />
                </Table.Td>
                <Table.Td class="whitespace-nowrap text-right min-w-[20px]">
                  <FormInput
                    type="text"
                    :value="numeroFormateado(it.total)"
                    disabled
                  />
                </Table.Td>
                <Table.Td class="whitespace-nowrap">
                  <span
                    class="px-3 py-1 rounded-full text-xs font-medium border border-slate-200/60 bg-white/60"
                    :class="badgeClassesEstadoItem(it.estado)"
                  >
                    {{ it.estado }}
                  </span>
                </Table.Td>
                <Table.Td class="whitespace-nowrap text-right">
                  <Button
                    v-if="(it.estado ?? '').toLowerCase() === 'pendiente'"
                    variant="danger"
                    size="sm"
                    class="px-2"
                    @click="eliminarItem(it)"
                  >
                    Eliminar
                  </Button>
                </Table.Td>
              </Table.Tr>
              <Table.Tr v-if="itemsLocal.length === 0">
                <Table.Td colspan="6" class="text-center py-8 text-slate-500">
                  No hay productos agregados.
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>

        <div class="mt-5 flex justify-between text-sm">
          <div class="text-slate-600">
            <div>Subtotal</div>
            <div class="mt-2">IVA</div>
            <div class="mt-2 font-semibold text-slate-800">Total</div>
          </div>
          <div class="text-right">
            <div>${{ numeroFormateado(subtotal, 2) }}</div>
            <div class="mt-2">${{ numeroFormateado(iva, 2) }}</div>
            <div class="mt-2 text-lg font-semibold text-rose-700">
              ${{ numeroFormateado(total, 2) }}
            </div>
          </div>
        </div>
      </Dialog.Description>

      <Dialog.Footer>
        <div class="flex w-full items-center justify-between gap-3">
          <Button variant="secondary" @click="emit('cerrar')">Cancelar</Button>
          <Button
            variant="primary"
            :disabled="guardando || itemsLocal.length === 0"
            @click="guardarYVolver"
          >
            {{ guardando ? "Guardando…" : "Cerrar" }}
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
